import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { useCallback, useEffect, useState } from 'react'
import { FiChevronLeft } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { getAnnounceList, getAnnounceTags } from 'services/announce'

const PAGE_SIZE = 5
const ALL_TAG: IAnnounceTag = { title: 'همه', key: '' }

function AnnounceCard({
    item,
    onDetail,
}: {
    item: IAnnounceItem
    onDetail: () => void
}) {
    const channels = item.channels?.map((c) => c.title).join('، ') || '—'
    return (
        <div className="mb-3 rounded-2xl border border-[#EFEFEF] bg-white p-4 shadow-[0_2px_10px_rgba(0,0,0,0.05)]">
            <div className="mb-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">عنوان:</span>
                <span className="rounded-lg bg-[#E8F5EC] px-3 py-1 text-xs text-green-1">
                    {item.tags}
                </span>
            </div>
            <div className="mb-2 flex items-center justify-between text-sm text-gray-500">
                <span>ارسال از طریق: {channels}</span>
                <span dir="ltr">{item.created_at?.split(' ')[0]}</span>
            </div>
            <div className="mb-3 text-sm leading-relaxed text-text-body">
                <span className="text-gray-500">متن پیام: </span>
                {item.text}
            </div>
            <button
                onClick={onDetail}
                className="flex items-center gap-1 text-sm text-green-1 active:scale-95"
            >
                جزئیات
                <FiChevronLeft className="h-4 w-4" />
            </button>
        </div>
    )
}

export default function AnnounceToMembers() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [tags, setTags] = useState<IAnnounceTag[]>([ALL_TAG])
    const [selectedKey, setSelectedKey] = useState<string>('')
    const [items, setItems] = useState<IAnnounceItem[]>([])
    const [page, setPage] = useState<number>(1)
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)

    // category chips (once). "همه" is always first (empty key = no filter).
    useEffect(() => {
        if (!id) return
        getAnnounceTags(id)
            .then((res) => setTags([ALL_TAG, ...(res || [])]))
            .catch(() => setTags([ALL_TAG]))
    }, [id])

    const loadPage = useCallback(
        async (key: string, pageNumber: number, reset: boolean) => {
            if (!id) return
            reset ? setLoading(true) : setLoadingMore(true)
            try {
                const res = await getAnnounceList(
                    id,
                    pageNumber,
                    key || undefined
                )
                const batch = res?.Announcements || []
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasMore(batch.length >= PAGE_SIZE)
            } catch {
                if (reset) setItems([])
                setHasMore(false)
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [id]
    )

    // (re)load page 1 whenever the selected category changes
    useEffect(() => {
        setPage(1)
        loadPage(selectedKey, 1, true)
    }, [selectedKey, loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(selectedKey, next, false)
    }

    return (
        <div className="flex h-full flex-col bg-white">
            <Header title="اطلاع‌رسانی به اعضا" />

            {/* category chips */}
            <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-[#EFEFEF] px-3 py-3">
                {tags.map((t) => {
                    const active = t.key === selectedKey
                    return (
                        <button
                            key={t.key || 'all'}
                            onClick={() => setSelectedKey(t.key)}
                            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                                active
                                    ? 'bg-green-1 text-white'
                                    : 'bg-main-bg text-text-body'
                            }`}
                        >
                            {t.title}
                        </button>
                    )
                })}
            </div>

            {/* list */}
            <div className="grow overflow-y-auto px-3 py-3">
                {loading ? (
                    <DotsLoading />
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400">
                        <p>در این دسته اطلاع‌رسانی‌ای ثبت نشده است</p>
                    </div>
                ) : (
                    <>
                        {items.map((item) => (
                            <AnnounceCard
                                key={item.announce_request_id}
                                item={item}
                                onDetail={() =>
                                    navigate(
                                        `/fund/announce/${id}/detail/${item.announce_request_id}`
                                    )
                                }
                            />
                        ))}
                        {hasMore && (
                            <button
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                className="my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                            >
                                {loadingMore
                                    ? 'در حال بارگذاری...'
                                    : 'موارد بیشتر'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
